import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrderMobileCardShell from "./OrderMobileCardShell";
import OrdersSubTabs from "./OrdersSubTabs";
import AuthService from "../../../api/services/AuthService";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import { MARGIN_SUB_TABS, BORROWING_TYPE_OPTIONS, MARGIN_ACCOUNT_TYPE_OPTIONS, buildMarginPairOptions } from "./marginOrderFilterConstants";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";

const ORDER_DESKTOP_COL_COUNT = 6;

const SERVICE_MAP = {
  borrow: (params) => AuthService.marginBorrowHistory(params),
  repay: (params) => AuthService.marginRepayHistory(params),
  interest: (params) => AuthService.marginInterestHistory(params),
};

const CROSS_SERVICE_MAP = {
  borrow: (params) => AuthService.crossBorrowHistory(params),
  repay: (params) => AuthService.crossRepayHistory(params),
  interest: (params) => AuthService.crossInterestHistory(params),
};

function formatPair(raw) {
  if (!raw) return "—";
  if (raw.includes("/")) return raw;
  if (raw.length >= 6) {
    const known = ["USDT", "BTC", "ETH", "BNB", "USDC"];
    for (const q of known) {
      if (raw.endsWith(q)) return `${raw.slice(0, raw.length - q.length)}/${q}`;
    }
  }
  return raw;
}

/** Unwrap MongoDB $numberDecimal objects. */
function dec(v) {
  if (v == null) return null;
  if (typeof v === "object" && v.$numberDecimal != null) return v.$numberDecimal;
  return v;
}
function safeNum(v, decimals = 8) {
  const n = parseFloat(dec(v));
  if (!Number.isFinite(n)) return "—";
  return parseFloat(n.toFixed(decimals)).toString();
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function toIsoFrom(dateStr) {
  return dateStr ? `${dateStr}T00:00:00.000Z` : undefined;
}
function toIsoTo(dateStr) {
  return dateStr ? `${dateStr}T23:59:59.999Z` : undefined;
}

function DatePickerCell({ value, onChange, ariaLabel, inputRef, placeholder = "From" }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">{value || placeholder}</span>
      <input ref={inputRef} type="date" className="so-dateCell__native" value={value} onChange={(e) => onChange(e.target.value)} aria-label={ariaLabel} />
    </div>
  );
}

const MarginBorrowingHistory = () => {
  const initRange = spotActivityDefaultLocalRange();

  const [accountType, setAccountType] = useState("isolated");
  const [txType, setTxType] = useState("borrow");
  const [contract, setContract] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pairOptions, setPairOptions] = useState([{ value: "all", label: "All" }]);
  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(rows);
  const [rawAccounts, setRawAccounts] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  useEffect(() => {
    AuthService.marginAccounts()
      .then((res) => {
        if (res?.success) {
          setRawAccounts(res.data || []);
          setPairOptions(buildMarginPairOptions(res.data || []));
        }
      })
      .catch(() => { });
  }, []);

  const resolvedPairId = contract === "all"
    ? undefined
    : rawAccounts.find((a) => `${a.base_asset}_${a.quote_asset}`.toLowerCase() === contract)?.pair_id;

  const loadHistory = useCallback(async (type, pairId, from, to) => {
    const svc = accountType === "cross" ? CROSS_SERVICE_MAP[type] : SERVICE_MAP[type];
    if (!svc) return;
    setLoading(true);
    try {
      const params = accountType === "cross"
        ? { start_date: toIsoFrom(from), end_date: toIsoTo(to) }
        : { pairId, from: toIsoFrom(from), to: toIsoTo(to) };
      const res = await svc(params);
      if (res?.success) {
        const raw = res.data || [];
        setRows(
          raw.map((r) => {
            const isInterest = type === "interest";
            // Cross ledger row shape uses: amount (signed), asset, created_at, interest_rate_daily
            const isCross = accountType === "cross";
            return {
              id: r.id || r._id || r.created_at,
              time: formatDate(r.created_at ?? r.time),
              pair: isCross ? (r.asset ?? "—") : formatPair(r.pair ?? r.contract),
              coin: r.asset ?? r.coin ?? "—",
              type: r.type ? (r.type.charAt(0).toUpperCase() + r.type.slice(1).toLowerCase()) : type,
              amount: isInterest
                ? safeNum(isCross ? r.interest_amount ?? Math.abs(parseFloat(r.amount || 0)) : (r.amount_charged ?? r.amount))
                : safeNum(isCross ? Math.abs(parseFloat(r.amount || 0)) : r.amount),
              loanAmount: isInterest ? safeNum(isCross ? r.debt_outstanding : r.loan_amount) : undefined,
              hourlyRate: isInterest ? (r.interest_rate_daily != null ? `${((r.interest_rate_daily / 24) * 100).toFixed(7)}%` : "—") : undefined,
              apr: isInterest ? (r.interest_rate_daily != null ? `${(r.interest_rate_daily * 100).toFixed(4)}%` : "—") : undefined,
              status: r.metadata?.fully_repaid === false ? "Partial" : "Completed",
            };
          })
        );
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [accountType]);

  useEffect(() => {
    loadHistory(txType, resolvedPairId, dateFrom, dateTo);
  }, [accountType, txType, resolvedPairId, dateFrom, dateTo, loadHistory]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      if (!(e.target instanceof Element)) return;
      if (!e.target.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpenDropdown(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const resetFilters = () => {
    const { from, to } = spotActivityDefaultLocalRange();
    setTxType("borrow"); setContract("all"); setDateFrom(from); setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") { try { el.showPicker(); return; } catch { /* */ } }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter borrowing history">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown id={`${pref}mbh-account`} label="Account" value={accountType} options={MARGIN_ACCOUNT_TYPE_OPTIONS}
            onChange={(v) => { setAccountType(v); setContract("all"); }} menuKey={`${pref}mbh-account`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          <CustomDropdown id={`${pref}mbh-type`} label="Type" value={txType} options={BORROWING_TYPE_OPTIONS} onChange={setTxType} menuKey={`${pref}mbh-type`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          {accountType === "isolated" && (
            <CustomDropdown id={`${pref}mbh-contract`} label="Contract" value={contract} options={pairOptions} onChange={setContract} menuKey={`${pref}mbh-contract`} openKey={openDropdown} onOpenToggle={setOpenDropdown} />
          )}
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} placeholder="From" ariaLabel="Start date" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">→</span>
              <DatePickerCell value={dateTo} onChange={setDateTo} placeholder="To" ariaLabel="End date" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TypeBadge = ({ type }) => {
    const t = String(type || "").toLowerCase();
    // if (t === "borrow")   return <span className="so-side--buy">{type}</span>;
    // if (t === "repay")    return <span className="so-side--sell">{type}</span>;
    // if (t === "interest") return <span className="so-table__td--muted">{type}</span>;
    return type;
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          {loading
            ? <div className="so-emptyState__text">Loading…</div>
            : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="mbh-title">
            <h1 id="mbh-title" className="so-page__title">Borrowing History</h1>
            <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
            {renderToolbar(false, "desk")}
            <div className="so-tableWrap">
              <table className="so-table so-table--rowLines">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Contract</th>
                    <th scope="col">Coin</th>
                    <th scope="col">Type</th>
                    {txType === "interest" ? (
                      <>
                        <th scope="col">Loan Amount</th>
                        <th scope="col">Amount Charged</th>
                        <th scope="col">Hourly Rate</th>
                        <th scope="col">APR</th>
                      </>
                    ) : (
                      <>
                        <th scope="col">Amount</th>
                        <th scope="col">Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? renderEmptyBody() : pagedRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.time}</td>
                      <td>{r.pair}</td>
                      <td>{r.coin}</td>
                      <td><TypeBadge type={r.type} /></td>
                      {txType === "interest" ? (
                        <>
                          <td className="so-num">{r.loanAmount}</td>
                          <td className="so-num">{r.amount}</td>
                          <td className="so-num">{r.hourlyRate}</td>
                          <td className="so-num">{r.apr}</td>
                        </>
                      ) : (
                        <>
                          <td className="so-num">{r.amount}</td>
                          <td><span className="so-badge--closed">{r.status}</span></td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Borrowing history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Borrowing History</h2>
          <OrdersSubTabs tabs={MARGIN_SUB_TABS} />
          <div className="so-mobile__toolbar">
            <SoMobileFilters modalId="mob-mbh-filters" toolbar={renderToolbar(true, "mob")} onReset={resetFilters} />
          </div>
          {rows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              {loading
                ? <div className="so-emptyState__text">Loading…</div>
                : <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />}
            </div>
          ) : pagedRows.map((r) => (
            <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Borrow details">
              <div className="so-mobileCard__row"><span>Time</span><span>{r.time}</span></div>
              <div className="so-mobileCard__row"><span>Contract</span><span>{r.pair}</span></div>
              <div className="so-mobileCard__row"><span>Coin</span><span>{r.coin}</span></div>
              <div className="so-mobileCard__row"><span>Type</span><TypeBadge type={r.type} /></div>
              {txType === "interest" ? (
                <>
                  <div className="so-mobileCard__row"><span>Loan Amount</span><span className="so-num">{r.loanAmount}</span></div>
                  <div className="so-mobileCard__row"><span>Amount Charged</span><span className="so-num">{r.amount}</span></div>
                  <div className="so-mobileCard__row"><span>Hourly Rate</span><span className="so-num">{r.hourlyRate}</span></div>
                  <div className="so-mobileCard__row"><span>APR</span><span className="so-num">{r.apr}</span></div>
                </>
              ) : (
                <>
                  <div className="so-mobileCard__row"><span>Amount</span><span className="so-num">{r.amount}</span></div>
                  <div className="so-mobileCard__row"><span>Status</span><span className="so-badge--closed">{r.status}</span></div>
                </>
              )}
            </OrderMobileCardShell>
          ))}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Borrowing history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default MarginBorrowingHistory;
